import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import {
  StatementRenderer,
  STATEMENT_ITEM_MARK,
  statementRowKey,
  type StatementItemMark,
} from "@/payroll/ai-script/statement/statement-renderer";
import { PackChatPackSkeleton } from "./pack-chat-skeletons";
import { PackChatState } from "./pack-chat-state";
import { isGeneralPayPackContext } from "./reference-label";
import type { IPackChatThreadSummary } from "./types";

/**
 * The pack the conversation is about, beside the conversation.
 *
 * Most threads ask whether one number is right, and answering them used to mean
 * leaving for the Grid tab. This is the reading pane's other half at wide widths:
 * the technician's current PayPack for this period, rendered by the same component
 * the technician reads. The thread keeps its filing run only as provenance; the
 * UI reads the current workbook.
 *
 * It is also the queue's index. Every figure that already has a figure-specific
 * thread carries that thread's mark, and its action selects that thread rather
 * than starting a second one about the same number. General PayPack threads do
 * not mark a figure.
 */
export const PackChatPackRail = ({
  businessId,
  runId,
  technicianId,
  threads,
  openThreadId,
  onSelectThread,
}: {
  businessId: string | null;
  /** The current workbook run for this period. */
  runId: string | null;
  technicianId: string | null;
  /** Every thread of this technician's, so each figure can show its own state. */
  threads: IPackChatThreadSummary[];
  /** The one being read, so its figure can be picked out of the pack. */
  openThreadId: string | null;
  onSelectThread: (threadId: string) => void;
}) => {
  const statementQuery = useGetAiScriptStatement(businessId, runId, {
    identity: STATEMENT_IDENTITY.technician,
    technicianId,
    enabled: Boolean(businessId && runId && technicianId),
  });

  // Keyed on the row's identity rather than the whole reference: the amount
  // changes on the rerun that fixes a questioned figure, and the thread has to stay
  // attached to its row across that.
  const threadsByRow = new Map<string, IPackChatThreadSummary>();
  threads.forEach((thread) => {
    if (isGeneralPayPackContext(thread.context)) return;

    const key = statementRowKey(thread.context);
    const existing = threadsByRow.get(key);
    // Two threads about one figure: the open one is the one a reader wants, and
    // between two of the same status the newer one.
    const better =
      !existing ||
      (thread.status === "open" && existing.status !== "open") ||
      (thread.status === existing.status &&
        thread.updated_at > existing.updated_at);
    if (better) threadsByRow.set(key, thread);
  });

  const marks: Record<string, StatementItemMark> = {};
  threadsByRow.forEach((thread, key) => {
    marks[key] =
      thread.status === "open"
        ? STATEMENT_ITEM_MARK.open
        : STATEMENT_ITEM_MARK.closed;
  });

  const statement = statementQuery.data?.statement;
  const openThread = threads.find((thread) => thread.id === openThreadId);

  // No header of its own: the pack renders the technician's name and the period,
  // which is all this column has to say about itself. StatementRenderer manages
  // its own overflow, so it only needs a definite height to scroll inside.
  return (
    <Stack gap="none" align="stretch" className="h-full min-h-0 min-w-0">
      {statementQuery.isLoading ? (
        <PackChatPackSkeleton />
      ) : statementQuery.isError ? (
        <PackChatState
          error={statementQuery.error}
          title="PayPack couldn't be loaded"
          body="Try again. If it keeps failing, contact support."
          compact
          action={
            <Button
              variant="secondary"
              size="sm"
              loading={statementQuery.isFetching}
              className="mt-2"
              onClick={() => void statementQuery.refetch()}
            >
              Try again
            </Button>
          }
        />
      ) : statement ? (
        <StatementRenderer
          statement={statement}
          // The conversation beside this already names the technician and the
          // period; the pack repeating it put a second title and a second rule a
          // few pixels off the one next door.
          showTitle={false}
          itemAction={{
            marks,
            // Which of the marked figures is the one on screen beside this. Two
            // marks look alike, and a reader following a conversation should not
            // have to match a label by eye to find the number it is about.
            activeRowKey:
              openThread && !isGeneralPayPackContext(openThread.context)
                ? statementRowKey(openThread.context)
                : null,
            // Marked figures only: this reader answers questions rather than
            // asking them, and an icon on all twenty rows would drown the two
            // marks that say where the work is.
            marksOnly: true,
            onAction: (reference) => {
              const thread = threadsByRow.get(statementRowKey(reference));
              if (thread) onSelectThread(thread.id);
            },
          }}
        />
      ) : (
        <Typography variant="bodyW3" textColor="tertiary" className="px-4">
          {/* Not an error: a technician who has not been linked to a ServiceTitan
              id has no pack to project, and the thread is still readable. */}
          This run has no pack for this technician.
        </Typography>
      )}
    </Stack>
  );
};
